import { faqPageSchema, breadcrumbSchema } from '../utils/structuredData';
import React from 'react';
import Accordion from '../components/Accordion';
import SEO from '../components/SEO';

const FAQPage = () => {
  return (
    <div style={styles.page}>
      <style>{`
        @media (max-width: 768px) {
          .subpage-hero-h1 { font-size: 30px !important; letter-spacing: 1px !important; }
          .subpage-hero-sub { font-size: 15px !important; }
          .subpage-hero { padding: 60px 0 !important; }
          .subpage-section-h2 { font-size: 24px !important; }
          .subpage-section-pad { padding: 60px 0 !important; }
          .subpage-cta-h2 { font-size: 24px !important; }
          .subpage-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      
      <SEO
        title="FAQ — Dealership Event Marketing Questions Answered"
        description="Answers about Driven2WIN's event services — pricing, process, guarantees, timelines, permit handling, and what results to expect."
        keywords="event marketing FAQ, dealership events questions, automotive events pricing, how offsite events work, tent event permits, direct mail dealership FAQ"
        canonicalUrl="https://driven2win.com/faq"
        structuredData={[faqPageSchema, breadcrumbSchema([{name:'Home',url:'https://driven2win.com'},{name:'FAQ',url:'https://driven2win.com/faq'}])]}
      />
      
      {/* Hero */}
      <section style={styles.hero}>
        <div style={styles.container}>
          <h1 style={styles.heroH1}>
            FREQUENTLY ASKED <span style={{color: '#FFC300'}}>QUESTIONS</span>
          </h1>
          <p style={styles.heroSubheadline}>
            Everything you need to know about working with Driven2WIN
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={styles.faqSection}>
        <div style={styles.container}>
          <Accordion 
            items={faqs.map(faq => ({
              title: faq.question,
              content: <p style={styles.answerText}>{faq.answer}</p>
            }))}
            defaultOpen={0}
          />
        </div>
      </section>

      {/* CTA */}
      <section style={styles.ctaSection}>
        <div style={styles.container}>
          <h2 style={styles.ctaH2}>
            Still Have <span style={{color: '#FFC300'}}>QUESTIONS?</span>
          </h2>
          <p style={styles.ctaText}>
            Call us at <strong>904-638-7200</strong> or fill out our contact form — we're here to help!
          </p>
        </div>
      </section>
    </div>
  );
};

// Data
const faqs = [
  {
    question: "What types of dealerships do you work with?",
    answer: "We specialize in automotive, golf cart, ATV, motorcycle, and RV events nationwide. Whether you sell cars, bikes, golf carts, or RVs, we have 23+ years of experience producing HIGH-IMPACT sales events that drive massive traffic and real results for these industries nationwide."
  },
  {
    question: "Do you work with car dealers, golf cart dealers, motorsports dealers, and RV dealers?",
    answer: "Yes! We work with ALL of these industries nationwide. Whether you sell cars, bikes, golf carts, or RVs — we've produced thousands of successful events across all these verticals. Our campaigns are tailored to your specific inventory and target market."
  },
  {
    question: "What geographic areas do you serve?",
    answer: "We serve dealerships in ALL 50 STATES. While our headquarters is in Jacksonville, FL, we are a NATIONWIDE company and have produced 6,500+ events from coast to coast. If you're in the United States, we can help you."
  },
  {
    question: "How much do your services cost?",
    answer: "Event costs vary based on scope, duration, market size, and services included (offsite setup, direct mail volume, social media budget, etc.). We offer Silver, Gold, and Platinum packages to fit different needs and budgets. Contact us for a FREE custom quote based on your specific goals and market."
  },
  {
    question: "What's included in your event packages?",
    answer: "Our packages include everything needed for a successful event: venue selection and coordination, ALL permits and compliance, professional tent setup (if offsite), direct mail campaigns, Facebook & Instagram promotion, on-site support, and post-event analysis. We handle EVERYTHING so you can focus on selling."
  },
  {
    question: "Do you guarantee results?",
    answer: "Yes! We GUARANTEE results. Average show rates are 60%+ on appointments made. If we don't deliver the numbers we promise, we keep working with you until you're satisfied. We stand behind our work 100%."
  },
  {
    question: "How far in advance should I book an event?",
    answer: "We recommend booking 6-8 weeks in advance to allow time for permit processing, venue coordination, and marketing campaigns. However, we can often accommodate rush events with 3-4 weeks notice depending on market availability and permit timelines."
  },
  {
    question: "What's your typical event timeline?",
    answer: "Most events run 3-7 days depending on your goals. The full process from booking to event completion is typically 6-10 weeks: Week 1-2 (planning & permits), Week 3-5 (marketing campaigns launch), Week 6-8 (event execution), Week 9-10 (post-event analysis and follow-up)."
  },
  {
    question: "Do I need to provide anything for the event?",
    answer: "All you need to provide is your inventory and sales team! We handle permits, tents, signage, promotion, appointment setting — literally EVERYTHING else. You focus on what you do best: closing deals. We take care of the rest."
  },
  {
    question: "How many appointments can I expect?",
    answer: "Results vary by market, event duration, and package level, but our AVERAGE event generates 125+ appointments. Our Gold package (5-day events) typically produces 100-150 appointments, while Platinum packages (7-day events) often generate 150-200+ appointments."
  },
  {
    question: "What's your average conversion rate?",
    answer: "Industry average for event-generated appointments is 40-50% show rate. Our events consistently achieve 60%+ show rates due to our multi-touch marketing approach (direct mail + social + SMS + email). Of those who show up, 70-80% are qualified buyers ready to make a deal."
  },
  {
    question: "How does your AI appointment setting work?",
    answer: "Our AI call center answers incoming calls 24/7, qualifies leads, and books appointments directly into your calendar. It handles overflow during peak times, captures after-hours inquiries, and ensures ZERO missed opportunities. The AI sounds natural, asks qualifying questions, and schedules appointments while you sleep!"
  },
  {
    question: "Can you handle multiple events for the same dealership?",
    answer: "Absolutely! Many of our clients run quarterly or bi-annual events with us. We'll help you plan an annual event calendar that maximizes impact while avoiding market fatigue. Regular events build anticipation and train your market to EXPECT your sales events."
  },
  {
    question: "What makes Driven2WIN different from other event companies?",
    answer: "Three things: (1) GUARANTEED RESULTS — we don't just promise numbers, we guarantee them. (2) COMPLETELY TURNKEY — we handle EVERYTHING from permits to promotion to setup. (3) PROVEN TRACK RECORD — 6,500+ events, 23+ years, $250K+ average gross per event. The numbers speak for themselves."
  },
  {
    question: "Do you require exclusive contracts?",
    answer: "No! We work on a per-event basis. If you love the results (and you will), you'll book us again. We earn your repeat business through PERFORMANCE, not contracts."
  },
  {
    question: "Can I see examples of your direct mail pieces?",
    answer: "Yes! Contact us and we'll send you samples of our high-performing mailer designs along with response rate data. We're proud of our creative work and happy to show you exactly what we'll be sending to YOUR market."
  },
  {
    question: "How do you handle bad weather during outdoor events?",
    answer: "All our tents are weather-resistant and professionally installed. For severe weather, we have contingency plans including event rescheduling insurance (optional), alternative indoor backup locations (venue dependent), and flexible scheduling to extend events if weather causes disruption."
  },
  {
    question: "What if I've had bad experiences with event companies before?",
    answer: "We hear this ALL THE TIME. Many dealerships have been burned by companies that overpromise and underdeliver. That's why we GUARANTEE our results, provide transparent reporting, maintain open communication throughout the process, and deliver EXACTLY what we promise. Read our testimonials — we turn skeptics into believers."
  }
];

// Styles
const styles = {
  page: {
    paddingTop: '109px',
  },
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '0 16px', // Mobile-first padding
  },
  hero: {
    background: 'linear-gradient(135deg, #1A1F3A 0%, #0F1118 100%)',
    padding: '60px 0', // Reduced for mobile
    textAlign: 'center',
  },
  heroH1: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '32px', // Mobile-first (auto-scales via global CSS)
    color: '#FFFFFF',
    marginBottom: '16px',
    letterSpacing: '2px',
  },
  heroSubheadline: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '18px', // Mobile-first
    color: '#E5E5E5',
    lineHeight: '1.6',
  },
  faqSection: {
    padding: '48px 0', // Mobile-first
    background: '#FFFFFF',
  },
  answerText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '16px',
    lineHeight: '1.7',
    color: '#FFFFFF', // White text on dark accordion background
    margin: 0,
  },
  ctaSection: {
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    padding: '60px 0', // Mobile-first
    textAlign: 'center',
  },
  ctaH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '32px', // Mobile-first (auto-scales)
    color: '#FFFFFF',
    marginBottom: '16px',
    letterSpacing: '2px',
  },
  ctaText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '18px', // Mobile-first
    color: '#FFFFFF',
    fontWeight: '600',
  },
};

export default FAQPage;
